import { useDisclosure } from "@chakra-ui/hooks";
import { Modal, ModalOverlay } from "@chakra-ui/modal";
import { changePaymentStatus } from "api/graphQL_payments";
import React from "react";
import { toast } from "react-hot-toast";
import { MdChangeCircle, MdHighlightOff } from "react-icons/md";
import Checkbox from "components/checkbox";
import DatePicker from "react-datepicker";
import moment from "moment";
import InputField from "components/fields/InputField";
import { Popover, PopoverTrigger, PopoverContent, PopoverHeader, PopoverArrow } from "@chakra-ui/react";
import { PANEL_STYLES } from "./InfoModal";

const ALL_STATUSES = [
  "APPROVED","REFUNDED","AUTHORIZED","CAPTURED","PENDING","CANCELLED","CREATED",
  "DECLINED","ERRORED","REFUND DECLINED","REJECTED","CHARGEBACK","ALERTED","HELD","DUPLICATE","DISPUTE",
];

const ChangeStatusModal = ({
  info, fetchPayments, transactionID, varient, enRefund, filterData, client_is_deleted,
}: {
  info?: any; fetchPayments: () => void; transactionID: string;
  varient?: string; enRefund?: any; filterData?: any; client_is_deleted?: boolean;
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [isLoading, setIsLoading] = React.useState<boolean>();
  const [isCloseModal, setIsCloseModal] = React.useState(false);
  const [form, setForm] = React.useState<any>({});

  const handleClose = () => {
    setForm({});
    setIsCloseModal(true);
    setTimeout(() => { setIsCloseModal(false); onClose(); }, 300);
  };

  const handleInput = (e: any) => {
    const { name, value } = e.target;
    setForm((p: any) => ({ ...p, [name]: value === "true" ? true : value === "false" ? false : value }));
  };

  const handleSubmit = async () => {
    if (!form?.change_status_to) { toast.error("Please select a status"); return; }
    if (form?.isChangeStatusDate && !form?.changeStatusDate) { toast.error("Please select a date"); return; }
    if (form?.change_status_to === "APPROVED" && (!form?.transaction_id || !form?.auth_id)) {
      toast.error("Please enter all required fields"); return;
    }
    setIsLoading(true);
    const payload = {
      arn_number: form?.ARN_Number,
      reason: form?.ReasonToChange,
      current_status: form?.change_status_to,
      change_status_date: form?.isChangeStatusDate
        ? moment(form.changeStatusDate).format("MM-DD-YYYY, HH:mm:ss")
        : moment(info?.created_at?.replace("GMT", "")).format("MM-DD-YYYY, HH:mm:ss"),
      transaction_id: (form?.change_status_to === "APPROVED" || form?.change_status_to === "CHARGEBACK")
        ? form?.transaction_id : "not_applicable",
      auth_id: form?.change_status_to === "APPROVED" ? form?.auth_id : "not_applicable",
    };
    changePaymentStatus(payload, transactionID)
      .then(() => { toast.success("Status updated successfully"); handleClose(); filterData?.(); })
      .catch((err: any) => toast.error(err?.response?.data?.error ?? "Something went wrong"))
      .finally(() => setIsLoading(false));
  };

  return (
    <>
      <Popover trigger="hover" placement="bottom">
        <PopoverTrigger>
          <button
            className="txn-action-btn"
            disabled={client_is_deleted}
            aria-label="Change status"
            onClick={() => {
              onOpen();
              setForm((p: any) => ({ ...p, transaction_id: info?.et_id === "not_applicable" ? "" : info?.et_id }));
            }}
          >
            <MdChangeCircle size={14} />
          </button>
        </PopoverTrigger>
        <PopoverContent border="1px solid #e9d5ff" borderRadius="12px" bg="#fdf4ff" maxW="210px" p="0" overflow="hidden" zIndex="20">
          <PopoverHeader bg="#fae8ff" fontSize="12px" fontWeight="700" color="#6d28d9" borderBottom="1px solid #e9d5ff" px="12px" py="9px">
            Change Status
          </PopoverHeader>
          <PopoverArrow bg="#fdf4ff" />
        </PopoverContent>
      </Popover>

      <Modal isOpen={isOpen} onClose={handleClose}>
        <ModalOverlay bg="rgba(15,23,42,0.55)" backdropFilter="blur(4px)" />
        <div className={`pconfirm-panel${isOpen && !isCloseModal ? " pconfirm-panel--open" : " pconfirm-panel--close"}`}>
          <div className="pconfirm-glow pconfirm-glow--purple" />
          <div className="pconfirm-header pconfirm-header--purple">
            <div className="pconfirm-header-brand">
              <div className="pconfirm-header-logo">
                <MdChangeCircle size={20} color="#fff" />
              </div>
              <div>
                <h2 className="pconfirm-title">Change Payment Status</h2>
                <p className="pconfirm-sub">Current: <strong>{info?.status}</strong></p>
              </div>
            </div>
            <button className="pconfirm-close" onClick={handleClose}><MdHighlightOff size={22} /></button>
          </div>

          <div className="pconfirm-body">
            {/* Current status */}
            <div className="csm-field-group">
              <label className="csm-label">Current Status</label>
              <div className="csm-status-display">{info?.status || "—"}</div>
            </div>

            {/* Change to */}
            <div className="csm-field-group">
              <label className="csm-label">
                Change Status To <span className="csm-req">*</span>
              </label>
              <select
                className="csm-select"
                name="change_status_to"
                value={form?.change_status_to ?? ""}
                onChange={handleInput}
              >
                <option value="">Select new status…</option>
                {ALL_STATUSES.map((s) => (
                  <option key={s} value={s} disabled={info?.status === s}>{s}</option>
                ))}
              </select>
            </div>

            {/* APPROVED extra fields */}
            {form?.change_status_to === "APPROVED" && (
              <div className="csm-field-group">
                <InputField
                  variant="auth" extra="w-full mb-2"
                  label="Transaction ID *" placeholder="Transaction ID"
                  id="transaction_id" type="text" name="transaction_id"
                  value={form?.transaction_id ?? ""} onChange={handleInput}
                />
                <InputField
                  variant="auth" extra="w-full"
                  label="Auth ID *" placeholder="Auth ID"
                  id="auth_id" type="text" name="auth_id"
                  value={form?.auth_id ?? ""} onChange={handleInput}
                />
              </div>
            )}

            {/* Date override toggle */}
            {form?.change_status_to && (
              <div className="csm-field-group">
                <label className="csm-checkbox-row">
                  <Checkbox
                    id="isChangeStatusDate"
                    value={!form?.isChangeStatusDate}
                    name="isChangeStatusDate"
                    checked={form?.isChangeStatusDate}
                    onChange={handleInput}
                    className="h-4 w-4"
                  />
                  <span className="csm-label" style={{ marginBottom: 0 }}>Override Status Date</span>
                </label>

                {form?.isChangeStatusDate && (
                  <>
                    <label className="csm-label">Current Date (read-only)</label>
                    <DatePicker
                      className="csm-datepicker"
                      selected={new Date(new Date(info?.created_at?.replace("GMT", "")).toUTCString())}
                      disabled
                      dateFormat="yyyy-MM-dd HH:mm:ss"
                      onChange={() => {}}
                    />
                    <label className="csm-label" style={{ marginTop: "10px" }}>
                      Change Date To <span className="csm-req">*</span>
                    </label>
                    <DatePicker
                      className="csm-datepicker"
                      placeholderText="Select new date"
                      selected={form?.changeStatusDate}
                      timeInputLabel="Time:"
                      dateFormat="yyyy-MM-dd HH:mm:ss"
                      onChange={(d) => setForm((p: any) => ({ ...p, changeStatusDate: d }))}
                      showTimeSelect
                      timeFormat="HH:mm:ss"
                      timeIntervals={15}
                    />
                  </>
                )}
              </div>
            )}

            {/* CHARGEBACK extra fields */}
            {form?.change_status_to === "CHARGEBACK" && (
              <>
                <div className="csm-field-group">
                  <label className="csm-label">ARN Number</label>
                  <input
                    className="csm-input"
                    type="text"
                    name="ARN_Number"
                    placeholder="Enter ARN number"
                    value={form?.ARN_Number ?? ""}
                    onChange={handleInput}
                  />
                </div>
                <div className="csm-field-group">
                  <label className="csm-label">Reason</label>
                  <textarea
                    className="csm-textarea"
                    name="ReasonToChange"
                    rows={3}
                    placeholder="Chargeback reason…"
                    value={form?.ReasonToChange ?? ""}
                    onChange={handleInput}
                  />
                </div>
              </>
            )}
          </div>

          <div className="pconfirm-footer">
            <button className="pconfirm-btn-cancel" onClick={handleClose}>Cancel</button>
            <button
              className="pconfirm-btn-action--purple"
              onClick={handleSubmit}
              disabled={!!isLoading || client_is_deleted}
            >
              {isLoading ? (
                <span className="pconfirm-spinner" />
              ) : (
                <><MdChangeCircle size={14} />Update Status</>
              )}
            </button>
          </div>
        </div>
      </Modal>

      {PANEL_STYLES}
    </>
  );
};

export default ChangeStatusModal;